import React, { Children, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Drawer,
} from '@biq/apex-ui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grid, Typography } from '@mui/material';
import {
    getQueryFromLocalStorage,
    HistoryQuery,
    SavedQueriesType,
} from '../../lib';
import { HistoryQueriesType } from '../lifegraph-explorer/components/lifegraph-filter/HistoryComponent/history-component-helper';
import {
    TQL_HISTORY_QUERY_KEY,
    tqlHistoryDrawerStyles,
} from './tql-editor-helper';

interface TQLHistoryDrawerProps {
    open: boolean;
    onClose: () => void;
    onApplyButtonClick: (query: string) => void;
}

export const TQLHistoryDrawer = ({
    open,
    onClose,
    onApplyButtonClick,
}: TQLHistoryDrawerProps): React.JSX.Element => {
    const [historyQueries, setHistoryQueries] = useState<HistoryQuery[]>(() =>
        getQueryFromLocalStorage(TQL_HISTORY_QUERY_KEY).map(
            (item: SavedQueriesType) => ({
                isExpanded: false,
                query: item.query,
                timestamp: item.timestamp,
            })
        )
    );

    const onChange = (index: number): void => {
        setHistoryQueries((prevQueries) =>
            prevQueries.map((item, i) => ({
                ...item,
                isExpanded: i === index ? !item.isExpanded : false,
            }))
        );
    };

    return (
        <Drawer
            closable
            open={open}
            drawerType='rounded'
            anchor='right'
            sx={tqlHistoryDrawerStyles.drawer}
            onClose={onClose}
            title={'TQL Editor History'}
            titleStyles={tqlHistoryDrawerStyles.title}
        >
            <Grid sx={tqlHistoryDrawerStyles.accordionContainer}>
                {historyQueries.length ? (
                    Children.toArray(
                        historyQueries.map(
                            (queryData: HistoryQueriesType, index: number) => {
                                return (
                                    <Accordion
                                        variant='outlined'
                                        expanded={queryData.isExpanded}
                                        sx={tqlHistoryDrawerStyles.fieldWidth}
                                        onChange={(): void => onChange(index)}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                        >
                                            {queryData.timestamp}
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={
                                                tqlHistoryDrawerStyles.accordionDetailsStyles
                                            }
                                        >
                                            <Grid
                                                sx={
                                                    tqlHistoryDrawerStyles.accordionDetailsContainerStyles
                                                }
                                            >
                                                <Grid
                                                    sx={
                                                        tqlHistoryDrawerStyles.contentContainerStyles
                                                    }
                                                >
                                                    <Typography
                                                        sx={
                                                            tqlHistoryDrawerStyles.queryTextStyles
                                                        }
                                                    >
                                                        {queryData.query}
                                                    </Typography>
                                                </Grid>
                                                <Grid
                                                    sx={
                                                        tqlHistoryDrawerStyles.buttonContainerStyles
                                                    }
                                                >
                                                    <Button
                                                        sx={
                                                            tqlHistoryDrawerStyles.buttonStyles
                                                        }
                                                        onClick={(): void =>
                                                            onApplyButtonClick(
                                                                queryData.query
                                                            )
                                                        }
                                                    >
                                                        Apply
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            }
                        )
                    )
                ) : (
                    <Grid sx={tqlHistoryDrawerStyles.emptyStateContainer}>
                        <Grid sx={tqlHistoryDrawerStyles.emptyStateTitleText}>
                            No History
                        </Grid>
                        <Grid sx={tqlHistoryDrawerStyles.emptyStateBodyText}>
                            Once you run queries, you’ll see your five most
                            recent queries here.
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Drawer>
    );
};
